﻿
/**
 * Utility class with convienent controller remote action results.
 */
import { Storage } from "./storage";
import { Element } from "./element";
import { Modal, ModalOptions } from "../modules/modal";
import Alert from "../modules/alert";
import Drawer from "../modules/drawer";
import { Focus } from "./focusfield";
import Confirmation from "../modules/confirmation";
import Ajax from "../modules/remote/ajax";

export class ActionResult {
  static openModal(data: ISimpleModalOptions): void {
    var $element = $(data.element);
    var modalOptions: ModalOptions = {
      content: data.content,
      alignment: data.alignment,
      $element: $element
    };
    if (data.anchorOnRemoteTarget && data.$remoteTarget) {
      modalOptions.$element = data.$remoteTarget;
    }

    if (ActionResult.isCurrentModalConfirmation())
      Modal.close();

    Modal.open(modalOptions);
    Drawer.closeAll();
  }

  static closeModal(data: any): void {
    if (data && data.message) {
      Alert.flash(data.message);
    }
    Modal.close();
    Drawer.closeAll();
  }

  public static closeModalById(data: ICloseModalByIdOptions): void {
    let $modal = Modal.current();
    if ($modal.length == 0)
      return;

    let isMatch = $modal.find('modal-id'.asDataSelector(data.modalId)).length > 0;

    if (isMatch)
      Modal.close();
  }

  static refresh(data): void {
    ActionResult.handleNavigationWarning(data);
    ActionResult.refreshAndFlashAlert(data);
  }
  static flashAlert(data: IFlashAlertOptions): void {
    Alert.flash(data.message, $(data.containerSelector));
  }
  static refreshAndFlashAlert(data?: IRefreshOptions): void {
    ActionResult.handleNavigationWarning(data);
    ActionResult.storeAlertAndCloseModals(data);
    Storage.set('scrollPositionBeforeReload', window.pageYOffset);
    window.location.reload();
  }
  static redirect(data: IRedirectOptions): void {
    ActionResult.handleNavigationWarning(data);
    ActionResult.storeAlertAndCloseModals(data);
    window.location.href = data.url;
  }
  static remoteRequest(data: IRedirectOptions): void {
    Ajax.execute({url: data.url});
  }
  static notImplemented(): void {
    Modal.open({
      content: '<div data-modal-id="not-implemented-modal" class="modal-sm"><div class="modal-header"><h2 class="modal-title">Not Implemented</h2><a class="button button-icon modal-title-close" data-dismiss="on">&#10006;</a></div>' +
        '<div class="modal-content"><p>This action has not been implemented.</p></div>' +
        '<div class="modal-actions"><a class="button button-raised button-primary modal-close href="javascript:;">Close</a></div></div>'
    });
  }
  static refreshContent(data: IRefreshContentOptions) {
    ActionResult.updateHtml(data, x => $(x.selector).html(x.html));
  }

  static replaceContent(data: IRefreshContentOptions) {
    ActionResult.updateHtml(data, x => $(x.selector).replaceWith($(x.html)));
  }

  static replaceValue(data: IRefreshValueOptions) {
    var element = new Element($(data.selector));

    if (data.shouldSuppressValueChanged)
      element.suppressValueChange(data.value);
    else
      element.value(data.value);
  }

  static replaceCallbackValue(data: IRefreshCallbackValueOptions) {
    Element.fromElements($(data.selector)).forEach((x) => {
      x.value(data.value);
    });
  }

  static appendContent(data: IAppendContentOptions) {
    if (!data || !data.selector)
      return;

    var $html = $(data.html);
    $(data.selector).append($html);
    window.Application.wireupConventions($html);
  }

  static prependContent(data: IPrependContentOptions) {
    if (!data || !data.selector)
      return;

    var $html = $(data.html);
    $(data.selector).prepend($html);
    window.Application.wireupConventions($html);
  }

  static refreshDropdownListItems(data: IRefreshDropdownListOptions) {
    if (!data || !data.selector)
      return;

    let element = new Element($(data.selector));

      element.suppressValueChange(function () {
      let value;
      if (data.shouldPreserveUserInput)
        value = element.value();

      element.$element.html(data.options)
        .trigger('dropdown:reload');

      if (data.shouldPreserveUserInput)
        element.value(value);
    });
  }

  static updateHtml(data: IRefreshContentOptions, replaceHtml: IHtmlReplacer) {
    $(document).trigger('htmlupdating.rustig.actionresult', data);

    let selectors = 'input:not([data-ms-real-input]), textarea, select, [data-file-field], [data-multiselect]';
    if (data.shouldPreserveUserInput) {
      var map = ActionResult.createMapOfInputFieldIds($(data.selector).find(selectors), data.shouldPreserveValidationErrors);

      Focus.after(() => replaceHtml(data));

      $(document).trigger('inputvaluesrestoring.rustig.actionresult', data);
      ActionResult.mapInputFieldIdsToValues($(data.selector).find(selectors), map, data.shouldPreserveValidationErrors);
      $(document).trigger('inputvaluesrestored.rustig.actionresult', data);
    }
    else {
      Focus.after(() => replaceHtml(data));
    }

    $(document).trigger('htmlupdated.rustig.actionresult', data);

    if (data && data.message) {
      Alert.flash(data.message);
    }
  }

  static renameThumbnail(data: IRenameThumbnail) {
    $(data.selector).each(function () {
      if (data.shouldReplaceTag) {
        var $name = $('<p data-dz-name title="' + data.name + '">' + data.name + '</p>');
        $(this).replaceWith($name);
      }
      else {
        if (this.tagName === 'P')
          this.textContent = data.name;
        else
          this.setAttribute('value', data.name);
        this.setAttribute('title', data.name);
      }
    });
  }

  static triggerEvent(data: ITriggerEventData): void {
    let $element = $(<any> document);

    if (data && data.selector)
      $element = $(data.selector);

    let event = $.Event(data.event);

    $element.trigger(data.event, data.data);
  }

  private static createMapOfInputFieldIds(inputFields: any, shouldPreserveValidationErrors: boolean): any {
    var map = {};
    for (var i = 0; i < inputFields.length; i++) {
      var $input = $(inputFields.get(i));
      if ($input.prop('id') || $input.data('mappable'))
        map[$input.prop('id')] = new InputFieldMapper($input, shouldPreserveValidationErrors);
    }
    return map;
  }

  private static mapInputFieldIdsToValues(inputFields: any, map: any, shouldPreserveValidationErrors: boolean): void {
    for (var i = 0; i < inputFields.length; i++) {
      var $input = $(inputFields.get(i));
      if (map[$input.prop('id')])
        map[$input.prop('id')].mapToInput($input, shouldPreserveValidationErrors);
    }
  }

  private static storeAlertAndCloseModals(data?: IRefreshOptions): void {
    if (data && data.message) {
      Storage.set(Alert.AlertStorageKey, data.message);
    }
    Modal.closeAll();
  }

  private static handleNavigationWarning(data?: IRefreshOptions): void {
    if (data.suppressNavigationWarning)
      $(window).off('beforeunload');
  }

  private static isCurrentModalConfirmation(): boolean {
    return Modal.current().find('#' + Confirmation.id).length > 0;
  }
}

export interface IRenameThumbnail {
  name: string;
  selector: string;
  shouldReplaceTag: boolean;
}

export interface IHtmlReplacer {
  (data: IRefreshContentOptions): void;
}

export interface ISimpleModalOptions {
  content: any;
  element: string;
  alignment: string;
  anchorOnRemoteTarget?: boolean;
  $remoteTarget?: JQuery;
}

export interface IFlashAlertOptions extends IRefreshOptions {
  containerSelector?: string;
}
export interface IRefreshOptions extends IAlertOptions {
  suppressNavigationWarning?: boolean;
}
export interface IRedirectOptions extends IRefreshOptions {
  url: string;
}
export interface IRefreshContentOptions extends IAlertOptions {
  html: string;
  selector: string;
  shouldPreserveUserInput: boolean;
  shouldPreserveValidationErrors: boolean;
}
export interface IRefreshValueOptions {
  value: string;
  selector: string;
  shouldSuppressValueChanged: boolean
}
export interface IRefreshCallbackValueOptions {
  value: string;
  selector: string;
}
export interface IAppendContentOptions {
  html: string;
  selector: string;
}
export interface IPrependContentOptions {
  html: string;
  selector: string;
}
export interface IAlertOptions {
  message?: string;
}
export interface IRefreshDropdownListOptions {
  selector: string;
  options: any;
  shouldPreserveUserInput?: boolean;
}

export interface ITriggerEventData {
  event: string,
  selector?: string,
  data?: any
}

export interface ICloseModalByIdOptions {
  modalId: string
}

class InputFieldMapper {
  value: any;
  hasValidationError;
  popoverOptions: any;

  constructor($inputField: any, shouldPreserveValidationErrors: boolean) {
    let element = new Element($inputField);

    this.value = element.value();

    if (shouldPreserveValidationErrors) {
      this.hasValidationError = element.$userFacingElement.hasClass('input-validation-error');
      if (this.hasValidationError)
          this.popoverOptions = element.$userFacingElement.data('rustig.popover.error').options;
    }
  }

  mapToInput($inputToMapTo: any, shouldPreserveValidationErrors: boolean) {
    let element = new Element($inputToMapTo);
      
      element.suppressValueChange(this.value);

    if (shouldPreserveValidationErrors) {
      if (this.hasValidationError && !element.$userFacingElement.hasClass('input-validation-error'))
        element.$userFacingElement.addClass('input-validation-error').popover(this.popoverOptions);
      else if (!this.hasValidationError && element.$userFacingElement.hasClass('input-validation-error'))
          element.$userFacingElement.removeClass('input-validation-error').popover('disable', 'error');
    }
  }
}